import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Avatar,
  Box,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import { handleFileUpload } from "utils/base64String";
import { useDispatch } from "react-redux";
import { updateProfile } from "store/actions";

export const EditProfileModal = ({ open, onClose, initialValues, userId, getUser }) => {
  const [previewImage, setPreviewImage] = useState(null);
  const dispatch = useDispatch();

  const validationSchema = Yup.object({
    name: Yup.string(),
    mobileNumber: Yup.string(),
    about: Yup.string(),
    website: Yup.string().url("Invalid URL"),
    // gender: Yup.string().oneOf(["male", "female", "other"]),
    dateOfBirth: Yup.date().nullable(),
  });

  useEffect(() => {
    if (initialValues?.profileImage) {
      setPreviewImage(initialValues.profileImage);
    }
  }, [initialValues]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Profile</DialogTitle>
      <Formik
        initialValues={{
          ...initialValues,
          dateOfBirth: initialValues.dateOfBirth,
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={async (values, actions) => {
          try {
            let base64ProfileImage = "";

            if (values?.profileImage?.name) {
              base64ProfileImage = await handleFileUpload(values.profileImage);
              const fileExtension = values.profileImage.type.split("/")[1];

              values.profileImage = {
                base64String: base64ProfileImage,
                extension: fileExtension,
                name: values.profileImage.name,
              };
            } else {
              delete values.profileImage;
            }

            const payload = {
              ...values,
              dateOfBirth: values.dateOfBirth
                ? new Date(values.dateOfBirth).toISOString()
                : null,
            };

            const callback = async (result) => {
              if (result?.code === 200 || result?.code === 201) {
                await getUser()
              } else {
                console.error("Submission failed:", result?.message || "Unknown error");
              }
            };
            dispatch(updateProfile(userId, payload, callback));

            actions.setSubmitting(false);
            onClose();
          } catch (error) {
            console.error("Error during submission:", error);
            actions.setSubmitting(false);
          }
        }
        }
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
          <Form>
            <DialogContent dividers>
              {/* Avatar Upload Section */}
              <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
                <Avatar
                  src={previewImage || ""}
                  alt="Profile"
                  sx={{ width: 80, height: 80, mb: 1 }}
                />
                <Button variant="outlined" component="label" size="small" sx={{ color: "#d32f2f", borderColor: "#d32f2f" }}>
                  Upload Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(event) => {
                      const file = event.currentTarget.files[0];
                      setFieldValue("profileImage", file);
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setPreviewImage(reader.result);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                </Button>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="name"
                    label="Full Name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="mobileNumber"
                    label="Mobile Number"
                    value={values.mobileNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.mobileNumber && Boolean(errors.mobileNumber)}
                    helperText={touched.mobileNumber && errors.mobileNumber}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="about"
                    label="About"
                    multiline
                    rows={3}
                    value={values.about}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="website"
                    label="Website"
                    value={values.website}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.website && Boolean(errors.website)}
                    helperText={touched.website && errors.website}
                  />
                </Grid>

                {/* <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      name="gender"
                      label="Gender"
                      value={values.gender}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.gender && Boolean(errors.gender)}
                      helperText={touched.gender && errors.gender}
                    >
                      {["male", "female", "other"].map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid> */}

                <Grid item xs={12} md={12}>
                  <div style={{ width: "100%" }}>
                    <DatePicker
                      selected={values.dateOfBirth}
                      onChange={(date) => setFieldValue("dateOfBirth", date)}
                      dateFormat="MM-dd-yyyy"
                      placeholderText="MM-DD-YYYY"
                      maxDate={new Date()}
                      customInput={
                        <TextField fullWidth label="Date of Birth" />
                      }
                      wrapperClassName="datePicker-fullWidth"
                    />
                  </div>

                  {touched.dateOfBirth && errors.dateOfBirth && (
                    <div style={{ color: "#d32f2f", fontSize: "0.75rem", marginTop: 4 }}>
                      {errors.dateOfBirth}
                    </div>
                  )}
                </Grid>

              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} color="inherit">
                Cancel
              </Button>
              <Button type="submit" className="create-btn" variant="contained">
                Save
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
